import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MessageSquareHeart, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { supabase } from '../supabase';
const categories = [{
  id: 'info',
  label: 'Informasi Struktural dan Kelembagaan BK'
}, {
  id: 'literasi',
  label: 'Literasi Media dan Informasi'
}, {
  id: 'etika',
  label: 'Etika Digital dan Keamanan Data'
}, {
  id: 'ux',
  label: 'Desain Media dan User Experience'
}, {
  id: 'wellbeing',
  label: 'Perkembangan Peserta Didik dan Well-being'
}];
interface Review {
  id: string;
  name: string;
  date: string;
  time: string;
  rating: number;
  comment: string;
}
export function RateUs() {
  const {
    user
  } = useAuth();
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);

  // 🔥 FETCH DATA
  useEffect(() => {
    fetchReviews();
  }, []);
  const fetchReviews = async () => {
    const {
      data,
      error
    } = await supabase.from('Rate_us') // ✅ FIX TABLE NAME
    .select('*').order('created_at', {
      ascending: false
    });
    if (error) {
      console.error('FETCH ERROR:', error.message);
      return;
    }
    const formatted = data.map((item: any) => ({
      id: item.id,
      name: item.Name,
      date: new Date(item.created_at).toLocaleDateString('id-ID'),
      time: new Date(item.created_at).toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      rating: item.Rating,
      comment: item.Coments
    }));
    setReviews(formatted);
  };
  const handleRating = (categoryId: string, value: number) => {
    setRatings(prev => ({
      ...prev,
      [categoryId]: value
    }));
  };
  const isFormValid = categories.every(cat => ratings[cat.id] > 0);

  // 🔥 SUBMIT
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      alert('Harus login dulu');
      return;
    }
    if (!isFormValid) {
      alert('Isi semua rating dulu');
      return;
    }
    setIsSubmitting(true);
    const total = Object.values(ratings).reduce((a, b) => a + b, 0);
    const avg = total / categories.length;
    const {
      data,
      error
    } = await supabase.from('Rate_us').insert([{
      Name: user.email,
      Rating: Number(avg.toFixed(1)),
      Coments: comment || 'Tanpa komentar'
    }]).select();
    console.log('INSERT RESULT:', data, error);
    if (error) {
      console.error('INSERT ERROR:', error.message);
      alert('Gagal kirim: ' + error.message);
    } else {
      setRatings({});
      setComment('');
      fetchReviews(); // refresh list
    }
    setIsSubmitting(false);
  };
  const renderStars = (rating: number, size = 16) => <div className="flex gap-1" data-id="element-509">
      {[1, 2, 3, 4, 5].map(star => <Star key={star} size={size} className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} data-id="element-510" />)}
    </div>;
  return <div className="min-h-screen bg-pastel-grey-light/30 py-12" data-id="element-511">
      <div className="max-w-4xl mx-auto px-4" data-id="element-512">

        {/* HEADER */}
        <div className="text-center mb-10" data-id="element-513">
          <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-514">
            <MessageSquareHeart className="w-8 h-8 text-yellow-600" data-id="element-515" />
          </div>

          <h1 className="text-3xl font-bold text-dark mb-2" data-id="element-516">
            Nilai Layanan Kami
          </h1>

          <p className="text-gray-600" data-id="element-517">
            Masukan Anda sangat berarti bagi kami
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8" data-id="element-518">

          {/* FORM */}
          <div className="lg:col-span-3 bg-white p-6 rounded-2xl shadow-sm" data-id="element-519">
            <form onSubmit={handleSubmit} data-id="element-520">
              <div className="space-y-5 mb-6" data-id="element-521">
                {categories.map(cat => <div key={cat.id} className="flex justify-between items-center" data-id="element-522">
                    <span className="text-sm" data-id="element-523">{cat.label}</span>

                    <div className="flex gap-1" data-id="element-524">
                      {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => handleRating(cat.id, star)} data-id="element-525">
                          <Star size={24} className={(ratings[cat.id] || 0) >= star ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} data-id="element-526" />
                        </button>)}
                    </div>
                  </div>)}
              </div>

              <textarea className="w-full border rounded-xl p-3 mb-4" value={comment} onChange={e => setComment(e.target.value)} placeholder="Tulis komentar..." data-id="element-527" />

              <Button type="submit" fullWidth isLoading={isSubmitting} disabled={!isFormValid} data-id="element-528">
                <Send size={16} className="mr-2" data-id="element-529" />
                Kirim Penilaian
              </Button>
            </form>
          </div>

          {/* REVIEW LIST */}
          <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm h-[500px] overflow-y-auto" data-id="element-530">
            <h2 className="font-bold mb-4" data-id="element-531">
              Ulasan ({reviews.length})
            </h2>

            <AnimatePresence data-id="element-532">
              {reviews.map(r => <motion.div key={r.id} className="mb-4 p-3 bg-gray-50 rounded-xl" data-id="element-533">
                  <div className="flex justify-between items-center mb-1" data-id="element-534">
                    <b className="text-sm" data-id="element-535">{r.name}</b>
                    {renderStars(r.rating, 12)}
                  </div>

                  <p className="text-xs text-gray-500" data-id="element-536">
                    {r.date} • {r.time}
                  </p>

                  <p className="text-sm mt-2" data-id="element-537">
                    {r.comment}
                  </p>
                </motion.div>)}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </div>;
}